import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Printer, Eye, FileDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatTime } from "@/lib/dates";
import { fetchRegistrationTeamNames } from "@/lib/registrationTeamNames";

/**
 * RLE Score Sheet Generator
 * Layout is IDENTICAL to the RecruitLook (RL) ScoreSheetGenerator print layout.
 * Data is wired to RLE tables instead of RL's tournament_matches:
 *   - games:    scheduled_games (for the event's published/latest schedule_version)
 *   - courts:   event_courts (court_id -> name)
 *   - teams:    teams (display_name, primary_coach_name)
 *   - rosters:  v_current_event_rosters (dynamic per registration/team)
 * Roster is populated dynamically by the teams actually in each game (by ID, no fuzzy matching).
 * RL branding (logo, www.RecruitLook.com, "Powered by RecruitLook") removed.
 */

interface ScoreSheetGeneratorProps {
  eventId: string;
  open: boolean;
  onClose: () => void;
}

interface GameData {
  id: string;
  scheduled_date: string | null;        // YYYY-MM-DD
  scheduled_start_time: string | null;   // HH:MM:SS
  division: string | null;
  court_name: string | null;
  team_a_registration_id: string | null;
  team_b_registration_id: string | null;
  home_team_registration_id: string | null;

  game_status: string | null;
  sort_order: number | null;
}

interface RosterPlayer {
  name: string;
  jersey: string;
}

const ScoreSheetGenerator: React.FC<ScoreSheetGeneratorProps> = ({ eventId, open, onClose }) => {
  const [eventName, setEventName] = useState('');
  const [eventDateLabel, setEventDateLabel] = useState('');
  const [games, setGames] = useState<GameData[]>([]);
  const [courts, setCourts] = useState<string[]>([]);
  const [divisions, setDivisions] = useState<string[]>([]);
  // Roster + team-name lookups keyed by registration id (primary) and team id (fallback)
  const [rosterByRegId, setRosterByRegId] = useState<Record<string, RosterPlayer[]>>({});
  const [teamNameByRegId, setTeamNameByRegId] = useState<Record<string, string>>({});
  const [coachByRegId, setCoachByRegId] = useState<Record<string, string>>({});

  // Filters
  const [courtFilter, setCourtFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedMatchIds, setSelectedMatchIds] = useState<Set<string>>(new Set());
  const [sheetType, setSheetType] = useState<'original' | 'blank'>('original');

  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !eventId) return;
    const load = async () => {
      // 1. Event + which schedule version to use (published, else latest)
      const { data: ev } = await supabase
        .from('events')
        .select('name, start_date, published_schedule_version_id')
        .eq('id', eventId)
        .maybeSingle();
      setEventName(ev?.name || 'Event');
      if (ev?.start_date) {
        const [y, m, d] = ev.start_date.split('-').map(Number);
        setEventDateLabel(new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
      }

      let versionId = ev?.published_schedule_version_id as string | null;
      if (!versionId) {
        const { data: ver } = await supabase
          .from('schedule_versions')
          .select('id')
          .eq('event_id', eventId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        versionId = ver?.id || null;
      }
      if (!versionId) { setGames([]); return; }

      // 2. Games for that version, with court name
      const { data: rawGames } = await supabase
        .from('scheduled_games')
        .select('id, scheduled_date, scheduled_start_time, division, game_status, sort_order, team_a_registration_id, team_b_registration_id, home_team_registration_id, court_id')
        .eq('schedule_version_id', versionId)
        .order('scheduled_date')
        .order('scheduled_start_time')
        .order('sort_order');

      // Resolve court names via separate lookup (avoids flaky PostgREST embed 404s)
      const courtNameById = new Map<string, string>();
      const { data: courtRows } = await supabase
        .from('event_courts')
        .select('id, name')
        .eq('event_id', eventId);
      (courtRows || []).forEach((c: any) => courtNameById.set(c.id, c.name));

      const gameData: GameData[] = (rawGames || [])
        .filter((g: any) => !['cancelled', 'bye'].includes(g.game_status))
        .map((g: any) => ({
          id: g.id,
          scheduled_date: g.scheduled_date,
          scheduled_start_time: g.scheduled_start_time,
          division: g.division,
          court_name: (g.court_id && courtNameById.get(g.court_id)) || null,
          team_a_registration_id: g.team_a_registration_id,
          team_b_registration_id: g.team_b_registration_id,
          home_team_registration_id: g.home_team_registration_id ?? g.team_a_registration_id,
          game_status: g.game_status,
          sort_order: g.sort_order,
        }));
      setGames(gameData);

      setCourts([...new Set(gameData.map(g => g.court_name).filter(Boolean))].sort() as string[]);
      setDivisions([...new Set(gameData.map(g => g.division).filter(Boolean))].sort() as string[]);

      const regIds = [...new Set(gameData.flatMap(g => [g.team_a_registration_id, g.team_b_registration_id]).filter(Boolean))] as string[];

      // 3. Team names + coach, both resolved from the registration.
      const nameByRegId = await fetchRegistrationTeamNames(regIds);
      const coachByReg: Record<string, string> = {};
      if (regIds.length) {
        const { data: regs } = await supabase
          .from('event_registrations_v2')
          .select('id, coach_first_name, coach_last_name')
          .in('id', regIds);
        (regs || []).forEach((r: any) => {
          const coach = [r.coach_first_name, r.coach_last_name].filter(Boolean).join(' ').trim();
          if (coach) coachByReg[r.id] = coach;
        });
      }
      setCoachByRegId(coachByReg);

      // 4. Dynamic rosters from the current-event-rosters view, keyed by registration.
      const rByReg: Record<string, RosterPlayer[]> = {};
      const { data: roster } = await supabase
        .from('v_current_event_rosters')
        .select('event_registration_id, team_name, player_name, jersey_number')
        .eq('event_id', eventId);
      (roster || []).forEach((p: any) => {
        const player: RosterPlayer = { name: p.player_name || '', jersey: p.jersey_number || '' };
        if (p.event_registration_id) (rByReg[p.event_registration_id] ||= []).push(player);
        if (p.event_registration_id && p.team_name && !nameByRegId[p.event_registration_id]) {
          nameByRegId[p.event_registration_id] = p.team_name;
        }
      });
      const byJersey = (a: RosterPlayer, b: RosterPlayer) => (parseInt(a.jersey) || 999) - (parseInt(b.jersey) || 999);
      Object.values(rByReg).forEach(list => list.sort(byJersey));
      setRosterByRegId(rByReg);
      setTeamNameByRegId({ ...nameByRegId });
    };
    load();
  }, [open, eventId]);

  const resolveName = (regId: string | null): string =>
    (regId && teamNameByRegId[regId]) || 'TBD';
  const resolveRoster = (regId: string | null): RosterPlayer[] =>
    (regId && rosterByRegId[regId]) || [];
  const resolveCoach = (regId: string | null): string => (regId && coachByRegId[regId]) || '';


  const dates = useMemo(() => [...new Set(games.map(g => g.scheduled_date).filter(Boolean))].sort() as string[], [games]);

  const matchFilter = useCallback((g: GameData) => {
    if (courtFilter !== 'all' && g.court_name !== courtFilter) return false;
    if (divisionFilter !== 'all' && g.division !== divisionFilter) return false;
    if (dateFilter !== 'all' && g.scheduled_date !== dateFilter) return false;
    return true;
  }, [courtFilter, divisionFilter, dateFilter]);

  const availableMatches = useMemo(() => games.filter(matchFilter), [games, matchFilter]);
  const filteredMatches = useCallback(
    () => availableMatches.filter(g => selectedMatchIds.size === 0 || selectedMatchIds.has(g.id)),
    [availableMatches, selectedMatchIds]
  );

  const toggleMatchSelection = (id: string) => setSelectedMatchIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const selectAllMatches = () => setSelectedMatchIds(new Set(availableMatches.map(m => m.id)));
  const clearMatchSelection = () => setSelectedMatchIds(new Set());

  const fmtDate = (d: string | null) => {
    if (!d) return '';
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : d;
  };
  const fmtTime = (t: string | null) => formatTime(t);

  // ── Print handler: identical to RL (landscape letter, inline-styled content) ──
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const printWindow = window.open('', '_blank', 'width=1100,height=850');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Score Sheets - Print</title>
<style>
@page { size: landscape letter; margin: 0.35in 0.25in; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; font-size: 8px; color: #000; }
.no-print { display: none !important; }
.score-sheet-page { page-break-after: always; break-after: page; page-break-inside: avoid; break-inside: avoid; padding: 2px 0; margin: 0; height: 100vh; max-height: 100vh; overflow: hidden; font-size: 7.5px; }
.score-sheet-page:last-child { page-break-after: avoid; break-after: avoid; }
table { border-collapse: collapse; }
td, th { border: 1px solid #000; padding: 1px 2px; }
img { display: block; }
.team-grid { display: grid; grid-template-columns: 1fr 120px; gap: 4px; overflow: hidden; }
.player-table { table-layout: fixed; width: 100%; }
.player-table th, .player-table td { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
</head><body>${printContent.innerHTML}</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 600);
  };

  const PLAYER_ROWS = 12;
  const sheetsToRender = sheetType === 'original' ? filteredMatches() : [null];

  // ── renderTeamSection: copied VERBATIM from RL for identical layout ──
  const renderTeamSection = (teamName: string, roster: RosterPlayer[], isBlank: boolean, coachName: string, homeAway: string = '') => {
    const rows = Array.from({ length: PLAYER_ROWS });
    const cellBorder = '1px solid #000';
    return (
      <div className="team-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '4px', fontSize: '8px', fontFamily: 'Arial, Helvetica, sans-serif', overflow: 'hidden' }}>
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', marginBottom: '2px', fontSize: '9px' }}>
            <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Team</span>
            <span style={{ flex: 1, borderBottom: cellBorder, textAlign: 'center', fontWeight: 600 }}>{!isBlank ? teamName : ' '}</span>
            <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Coach</span>
            <span style={{ flex: 1, borderBottom: cellBorder, textAlign: 'center' }}>{!isBlank ? coachName : ' '}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '8px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                HOME
                <span style={{ width: '11px', height: '11px', border: cellBorder, display: 'inline-block', textAlign: 'center', lineHeight: '11px', fontSize: '9px' }}>{homeAway === 'HOME' ? 'X' : ''}</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                AWAY
                <span style={{ width: '11px', height: '11px', border: cellBorder, display: 'inline-block', textAlign: 'center', lineHeight: '11px', fontSize: '9px' }}>{homeAway === 'AWAY' ? 'X' : ''}</span>
              </span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px', fontSize: '8px', overflow: 'hidden', maxWidth: '100%' }}>
            <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}><u>Team Fouls:</u> 1st</span>
            <div style={{ display: 'flex', flexShrink: 0 }}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <div key={n} style={{ width: '14px', height: '14px', border: cellBorder, textAlign: 'center', lineHeight: '14px', fontSize: '8px' }}>{n}</div>
              ))}
            </div>
            <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>2nd</span>
            <div style={{ display: 'flex', flexShrink: 0 }}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <div key={n} style={{ width: '14px', height: '14px', border: cellBorder, textAlign: 'center', lineHeight: '14px', fontSize: '8px' }}>{n}</div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', fontSize: '9px' }}>
            <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}><u>Timeouts:</u></span>
            <span>Full</span>
            <span style={{ width: '36px', borderBottom: cellBorder, display: 'inline-block' }}>&nbsp;</span>
            <span>30-sec</span>
            <span style={{ width: '36px', borderBottom: cellBorder, display: 'inline-block' }}>&nbsp;</span>
            <span>30-sec</span>
            <span style={{ width: '36px', borderBottom: cellBorder, display: 'inline-block' }}>&nbsp;</span>
            <span style={{ whiteSpace: 'nowrap' }}>OT 30-sec</span>
            <span style={{ width: '36px', borderBottom: cellBorder, display: 'inline-block' }}>&nbsp;</span>
          </div>
          <table className="player-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '8px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '5%' }} /><col style={{ width: '27%' }} /><col style={{ width: '10%' }} />
              <col style={{ width: '22%' }} /><col style={{ width: '22%' }} /><col style={{ width: '14%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ border: cellBorder, padding: '1px 2px', textAlign: 'center', fontWeight: 'bold' }}>#</th>
                <th style={{ border: cellBorder, padding: '1px 2px', textAlign: 'center', fontWeight: 'bold' }}>Name</th>
                <th style={{ border: cellBorder, padding: '1px 2px', textAlign: 'center', fontWeight: 'bold' }}>Fouls</th>
                <th style={{ border: cellBorder, padding: '1px 2px', textAlign: 'center', fontWeight: 'bold' }}>1st Half</th>
                <th style={{ border: cellBorder, padding: '1px 2px', textAlign: 'center', fontWeight: 'bold' }}>2nd Half</th>
                <th style={{ border: cellBorder, padding: '1px 2px', textAlign: 'center', fontWeight: 'bold' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((_, ri) => (
                <tr key={ri}>
                  <td style={{ border: cellBorder, padding: '1px 2px', height: '17px', textAlign: 'center', fontSize: '9px' }}>
                    {roster[ri] ? (roster[ri].jersey || (ri + 1)) : ''}
                  </td>
                  <td style={{ border: cellBorder, padding: '1px 2px', height: '17px', fontSize: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {roster[ri]?.name || ''}
                  </td>
                  <td style={{ border: cellBorder, padding: '0px', height: '17px', textAlign: 'center', lineHeight: 1 }}>
                    <div style={{ fontSize: '7px', lineHeight: '8px' }}>1 2 3</div>
                    <div style={{ fontSize: '7px', lineHeight: '8px' }}>4 5 6</div>
                  </td>
                  <td style={{ border: cellBorder, padding: '1px 2px', height: '17px' }}></td>
                  <td style={{ border: cellBorder, padding: '1px 2px', height: '17px' }}></td>
                  <td style={{ border: cellBorder, padding: '1px 2px', height: '17px' }}></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ flexShrink: 0, width: '120px', overflow: 'hidden' }}>
          <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '2px', fontSize: '8px' }}>Running Score</div>
          <table style={{ borderCollapse: 'collapse', fontSize: '8px' }}>
            <tbody>
              {Array.from({ length: 20 }).map((_, ri) => (
                <tr key={ri}>
                  {[0, 20, 40, 60, 80].map(offset => {
                    const num = ri + 1 + offset;
                    return (
                      <td key={offset} style={{ border: cellBorder, width: '22px', textAlign: 'center', fontWeight: 500, height: '12px', lineHeight: '12px', fontSize: '8px' }}>{num}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderScoreSheet = (game: GameData | null, idx: number) => {
    const isBlank = !game;
    const team1 = isBlank ? '' : resolveName(game!.team_a_registration_id);
    const team2 = isBlank ? '' : resolveName(game!.team_b_registration_id);
    const team1Roster = isBlank ? [] : resolveRoster(game!.team_a_registration_id);
    const team2Roster = isBlank ? [] : resolveRoster(game!.team_b_registration_id);
    const team1Coach = isBlank ? '' : resolveCoach(game!.team_a_registration_id);
    const team2Coach = isBlank ? '' : resolveCoach(game!.team_b_registration_id);
    const matchDate = isBlank ? '' : fmtDate(game!.scheduled_date);
    const matchTime = isBlank ? '' : fmtTime(game!.scheduled_start_time);
    const courtDisplay = isBlank ? '' : (game!.court_name || '');
    const divName = isBlank ? '' : (game!.division && game!.division !== '(unspecified)' ? game!.division : '');
    const homeId = isBlank ? null : game!.home_team_registration_id;
    const team1HomeAway = !homeId ? '' : (homeId === game!.team_a_registration_id ? 'HOME' : 'AWAY');
    const team2HomeAway = !homeId ? '' : (homeId === game!.team_b_registration_id ? 'HOME' : 'AWAY');


    return (
      <div key={idx} className="score-sheet-page" style={{
        fontFamily: 'Arial, Helvetica, sans-serif', padding: '2px 0', margin: 0, backgroundColor: '#fff',
        border: '1px solid #ccc', borderRadius: '4px',
        pageBreakAfter: idx === sheetsToRender.length - 1 ? 'auto' : 'always', pageBreakInside: 'avoid',
      }}>
        {/* EVENT HEADER — branding removed; event name only */}
        <div style={{ textAlign: 'center', marginBottom: '2px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '12px', lineHeight: 1.2 }}>{eventName}</div>
          {eventDateLabel && <div style={{ color: '#666', fontSize: '9px' }}>{eventDateLabel}</div>}
          <div style={{ fontSize: '9px', marginTop: '2px' }}>Division: {divName || '____________________'}</div>
        </div>

        {/* MATCH INFO */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '3px' }}>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '10px' }}>Official Score Sheet</div>
            {divName && <div style={{ fontWeight: 600, fontSize: '9px' }}>Division: {divName}</div>}
          </div>
          <div style={{ textAlign: 'right', fontSize: '9px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end', marginBottom: '1px' }}>
              <span style={{ fontWeight: 'bold' }}>Date</span>
              <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '80px', textAlign: 'center' }}>{matchDate}</span>
              <span style={{ fontWeight: 'bold', marginLeft: '6px' }}>Time</span>
              <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '45px', textAlign: 'center' }}>{matchTime}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
              <span style={{ fontWeight: 'bold' }}>Court</span>
              <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '150px', textAlign: 'center' }}>{courtDisplay}</span>
            </div>
          </div>
        </div>

        {/* FINAL SCORE — prominent, hand-filled official record line */}
        <div style={{ border: '3px solid #000', borderRadius: '4px', padding: '4px 8px', margin: '4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '13px', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>FINAL SCORE</div>
          <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-around', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
              <span style={{ fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '45%' }}>{team1 || '____________'}</span>
              <span style={{ border: '2px solid #000', borderRadius: '3px', minWidth: '70px', height: '34px', flex: 1, maxWidth: '110px' }}></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
              <span style={{ fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '45%' }}>{team2 || '____________'}</span>
              <span style={{ border: '2px solid #000', borderRadius: '3px', minWidth: '70px', height: '34px', flex: 1, maxWidth: '110px' }}></span>
            </div>
          </div>
        </div>

        {renderTeamSection(team1, team1Roster, isBlank, team1Coach, team1HomeAway)}
        <div style={{ margin: '2px 0', borderTop: '1px solid #000' }}></div>
        {renderTeamSection(team2, team2Roster, isBlank, team2Coach, team2HomeAway)}

        {/* FOOTER — "Powered by RecruitLook" removed */}
        <div style={{ marginTop: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '7px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <span>Referee: ________________________</span>
            <span>Score Verified By: ________________________</span>
          </div>
        </div>
      </div>
    );
  };

  const matchCount = filteredMatches().length;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>Score Sheet Generator</SheetTitle></SheetHeader>
          <div className="space-y-5 mt-4">
            <div>
              <Label>Court</Label>
              <Select value={courtFilter} onValueChange={setCourtFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courts</SelectItem>
                  {courts.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Division</Label>
              <Select value={divisionFilter} onValueChange={setDivisionFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Divisions</SelectItem>
                  {divisions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Day</Label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Days</SelectItem>
                  {dates.map(d => <SelectItem key={d} value={d}>{fmtDate(d)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {sheetType === 'original' && availableMatches.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Select Matches</Label>
                  <div className="flex gap-2">
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={selectAllMatches}>Select All</Button>
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={clearMatchSelection}>Clear</Button>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mb-1">
                  {selectedMatchIds.size === 0 ? 'All matches included (no selection = all)' : `${selectedMatchIds.size} selected`}
                </p>
                <ScrollArea className="h-40 border rounded-md p-2">
                  <div className="space-y-1">
                    {availableMatches.map(m => {
                      const label = `${resolveName(m.team_a_registration_id)} vs ${resolveName(m.team_b_registration_id)}`;
                      const meta = `${m.court_name ? ' · ' + m.court_name : ''}${m.scheduled_start_time ? ' · ' + fmtTime(m.scheduled_start_time) : ''}`;
                      return (
                        <label key={m.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                          <Checkbox checked={selectedMatchIds.has(m.id)} onCheckedChange={() => toggleMatchSelection(m.id)} />
                          <span className="truncate">{label}{meta}</span>
                        </label>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}

            <div>
              <Label>Score Sheet Type</Label>
              <Select value={sheetType} onValueChange={(v) => setSheetType(v as 'original' | 'blank')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Original (Pre-filled)</SelectItem>
                  <SelectItem value="blank">Blank Template</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="secondary">{sheetType === 'blank' ? '1 blank sheet' : `${matchCount} match${matchCount !== 1 ? 'es' : ''}`}</Badge>
              {Object.keys(rosterByRegId).length > 0 && (
                <Badge variant="outline" className="text-green-600 border-green-300">rosters loaded</Badge>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={() => setShowPreview(true)} disabled={sheetType === 'original' && matchCount === 0} className="flex-1">
                <Eye className="h-4 w-4 mr-2" /> Preview
              </Button>
            </div>

            {sheetType === 'original' && matchCount === 0 && games.length === 0 && (
              <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                No games found for this event. Generate a schedule first.
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="flex items-center justify-between">
              <span>Score Sheet Preview</span>
              <div className="flex gap-2">
                <Button size="sm" onClick={handlePrint}><Printer className="h-4 w-4 mr-1" /> Print All</Button>
                <Button size="sm" variant="outline" onClick={handlePrint}><FileDown className="h-4 w-4 mr-1" /> Download PDF</Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div ref={printRef} id="score-sheet-preview-portal">
            {sheetsToRender.map((m, i) => renderScoreSheet(m, i))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScoreSheetGenerator;
